import { motion } from 'framer-motion';
import { fadeUp, fadeLeft, fadeRight, stagger, viewport } from '../../utils/animations';
import { HiArrowDownTray, HiArrowUpRight, HiCheckCircle } from 'react-icons/hi2';
import AVATAR from '../../images/ujjal.jpg';

const SKILL_GROUPS = [
  {
    label: 'Front-End',
    color: '#818cf8',
    bg: 'rgba(99,102,241,0.1)',
    border: 'rgba(99,102,241,0.25)',
    skills: ['React', 'Next.js', 'TypeScript', 'Angular', 'Tailwind CSS', 'Redux / RTK', '+ more'],
  },
  {
    label: 'Back-End',
    color: '#a78bfa',
    bg: 'rgba(139,92,246,0.1)',
    border: 'rgba(139,92,246,0.25)',
    skills: ['Node.js', 'Express.js', 'REST APIs', 'GraphQL', 'MongoDB', 'PostgreSQL', 'Firebase', 'Prisma ORM', 'AWS', '+ more'],
  },
  {
    label: 'Mobile & Tools',
    color: '#67e8f9',
    bg: 'rgba(6,182,212,0.1)',
    border: 'rgba(6,182,212,0.25)',
    skills: ['React Native', 'Expo', 'AWS EC2', 'Docker', 'GitHub Actions', 'Jest', 'Figma', 'CI/CD', 'Tamagui','NativeWind', '+ more'],
  },
];

const HIGHLIGHTS = [
  'Open-source contributor',
  'Hybrid/Remote work mindset',
  'Test-driven development',
  'Performance optimization',
];

export default function About() {
  return (
    <section id="about" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 65% 55% at 85% 25%, rgba(99,102,241,0.04), transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        <motion.div variants={stagger(0.1)} initial="hidden" whileInView="show" viewport={viewport} className="mb-14">
          <motion.div variants={fadeUp} className="section-label">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">01 / About me</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight max-w-2xl">
            Building for humans,<br /><span className="gradient-text">engineered for impact</span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-[380px_1fr] xl:grid-cols-[420px_1fr] gap-12 xl:gap-16 items-start">

          <motion.div
            variants={fadeLeft} initial="hidden" whileInView="show" viewport={viewport}
            className="space-y-4"
          >
            <div className="relative group">
              <div className="absolute -inset-2.5 rounded-[36px] opacity-25 blur-2xl"
                style={{ background: 'linear-gradient(135deg, #6366f1, #a78bfa)' }} />
              <div className="relative overflow-hidden rounded-[28px] border border-white/[0.07]">
                <img
                  src={AVATAR}
                  alt="Ujjal Zaman"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(5,5,8,0.8) 100%)' }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="relative flex h-2 w-2">
                      <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <span className="text-sm font-semibold text-white">Open to hybrid/remote roles</span>
                  </div>
                  <p className="font-mono text-xs text-zinc-600">New York, USA</p>
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-4 border border-white/[0.06] grid grid-cols-2 gap-2">
              {HIGHLIGHTS.map(h => (
                <div key={h} className="flex items-center gap-2">
                  <HiCheckCircle className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                  <span className="text-xs text-zinc-500">{h}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={stagger(0.1)} initial="hidden" whileInView="show" viewport={viewport}
            className="space-y-8"
          >
            <motion.div variants={fadeRight} className="space-y-3">
              <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
                I'm a passionate <span className="text-white font-semibold">Full-Stack Developer</span> based in
                New York, USA, with <span className="text-indigo-400 font-medium">4+ years proven working</span> experience in building production-grade web and mobile applications.
              </p>
              <p className="text-xs sm:text-base text-zinc-500 leading-relaxed">
                I specialize in the React ecosystem with deep backend expertise in Node.js &amp; databases.
                I love turning complex problems into elegant, performant products that delight users
                at every interaction.
              </p>
            </motion.div>

            <motion.div variants={fadeRight} className="space-y-4">
              <p className="font-mono text-[10px] text-zinc-700 uppercase tracking-[0.2em] font-bold">Technical Expertise</p>

              <div className="space-y-3">
                {SKILL_GROUPS.map((group) => (
                  <div key={group.label} className="flex gap-3 items-start">
                    <span
                      className="flex-shrink-0 font-mono text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-1.5 rounded-xl mt-0.5 text-center min-w-[68px]"
                      style={{ color: group.color, background: group.bg, border: `1px solid ${group.border}` }}
                    >
                      {group.label}
                    </span>

                    <div className="flex flex-wrap gap-1.5">
                      {group.skills.map(s => (
                        <span
                          key={s}
                          className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium
                                     bg-white/[0.04] border border-white/[0.07] text-zinc-400
                                     hover:border-white/20 hover:text-white hover:bg-white/[0.07]
                                     transition-all duration-200 cursor-default whitespace-nowrap"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeRight} className="space-y-3">
              <p className="text-sm sm:text-base text-zinc-500 leading-relaxed">
                I'm a <span className="text-indigo-400/90 font-medium">quick learner</span> and pick up new languages, tools, and technologies easily—so I can ship clean, polished UIs and maintainable code no matter the stack.
              </p>
            </motion.div>

        
            <motion.div variants={fadeRight} className="flex flex-wrap items-center gap-3">
              <a
                href="https://drive.google.com/file/d/1GEYvUD7g0UEldrNOwuPBe8oGIH16bJir/view"
                target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 px-5 py-3 rounded-2xl text-sm font-semibold
                  text-white bg-indigo-600 hover:bg-indigo-500 shadow-glow-sm hover:shadow-glow-md
                  transition-all duration-200"
              >
                <HiArrowDownTray className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                Download Resume
              </a>
              <a
                href="https://github.com/Ujjalzaman"
                target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium
                  text-zinc-400 glass border border-white/[0.08] hover:border-white/20 hover:text-white
                  transition-all duration-200"
              >
                GitHub
                <HiArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
